// T3-B PR 4 (2026-05-12) / PR 4-bis (2026-05-12):
//   tier preset + template set + template motif の store (read + CRUD)。
//   PR 4 では sponsored 入稿 form の dropdown 用 read-only、PR 4-bis で CRUD method を追加。
//
// API: omikuji-api PR #117 / #118。

import { FortuneTierPresetApi, MotifTemplateSetApi, TemplateMotifApi } from '@/apis';
import { ResponseData } from '@/types/http';
import { flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';

export interface TierDefinition {
    value: number,
    label: string,
    weight?: number,
    color?: string | null,
    icon_url?: string | null
}

export interface FortuneTierPreset {
    id: number,
    preset_key: string,
    name: string,
    description?: string | null,
    tier_definitions: TierDefinition[],
    is_active: boolean
}

export interface MotifTemplateSet {
    id: number,
    name: string,
    slug: string,
    description?: string | null,
    tier_preset_id: number | null,
    tier_preset?: { id: number, preset_key: string, name: string } | null,
    supervisor_required: boolean,
    supervisor_info?: string | null,
    donation_pct?: number,
    is_active: boolean,
    display_order: number
}

export interface TemplateMotif {
    id: number,
    template_set_id: number,
    template_set_name?: string | null,
    template_set_slug?: string | null,
    name: string,
    slug: string,
    image_url: string,
    default_tier_value: number,
    default_fortune_text?: string | null,
    sponsor_purchase_disabled: boolean,
    postcard_image_url?: string | null,
    display_order: number,
    is_active: boolean
}

export default class TierCatalogStore extends BaseStore {
    tierPresets: FortuneTierPreset[] = [];
    templateSets: MotifTemplateSet[] = [];
    templateMotifs: TemplateMotif[] = [];
    loaded: boolean = false;

    presetApi: FortuneTierPresetApi;
    templateSetApi: MotifTemplateSetApi;
    templateMotifApi: TemplateMotifApi;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            tierPresets: observable,
            templateSets: observable,
            templateMotifs: observable,
            loaded: observable,
            loadAll: flow.bound,
            reloadTemplateMotifs: flow.bound,
            // PR 4-bis CRUD
            listTierPresets: flow.bound,
            createTierPreset: flow.bound,
            updateTierPreset: flow.bound,
            removeTierPreset: flow.bound,
            listTemplateSets: flow.bound,
            createTemplateSet: flow.bound,
            updateTemplateSet: flow.bound,
            removeTemplateSet: flow.bound,
            listTemplateMotifs: flow.bound,
            createTemplateMotif: flow.bound,
            updateTemplateMotif: flow.bound,
            removeTemplateMotif: flow.bound
        });
        this.presetApi = new FortuneTierPresetApi();
        this.templateSetApi = new MotifTemplateSetApi();
        this.templateMotifApi = new TemplateMotifApi();
    }

    *loadAll(force: boolean = false) {
        if (this.loaded && !force) return;
        try {
            const [presetRes, setRes, motifRes]: [ResponseData, ResponseData, ResponseData] = yield Promise.all([
                this.rootStore.apiStore.call(this.presetApi, this.presetApi.list, undefined),
                this.rootStore.apiStore.call(this.templateSetApi, this.templateSetApi.list, undefined),
                this.rootStore.apiStore.call(this.templateMotifApi, this.templateMotifApi.list, undefined)
            ]);
            if (presetRes?.ok) this.tierPresets = presetRes.data?.items ?? [];
            if (setRes?.ok) this.templateSets = setRes.data?.items ?? [];
            if (motifRes?.ok) this.templateMotifs = motifRes.data?.items ?? [];
            this.loaded = true;
        } catch (_e) {
            // silent
        }
    }

    *reloadTemplateMotifs(templateSetId?: number | string) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.templateMotifApi,
                this.templateMotifApi.list,
                templateSetId ? { template_set_id: templateSetId } : undefined
            );
            if (res?.ok) this.templateMotifs = res.data?.items ?? [];
        } catch (_e) {
            // silent
        }
    }

    getPresetById(id?: number | string | null): FortuneTierPreset | undefined {
        if (id == null) return undefined;
        return this.tierPresets.find(p => Number(p.id) === Number(id));
    }

    // ============ PR 4-bis (2026-05-12) CRUD ============

    *listTierPresets(payload?: { include_inactive?: 0 | 1 }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.presetApi, this.presetApi.list, payload);
            if (res?.ok) this.tierPresets = res.data?.items ?? [];
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *createTierPreset(payload: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.presetApi, this.presetApi.create, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *updateTierPreset(payload: { id: string | number, data: any }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.presetApi, this.presetApi.update, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *removeTierPreset(id: string | number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.presetApi, this.presetApi.remove, id);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *listTemplateSets(payload?: { include_inactive?: 0 | 1 }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.templateSetApi, this.templateSetApi.list, payload);
            if (res?.ok) this.templateSets = res.data?.items ?? [];
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *createTemplateSet(payload: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.templateSetApi, this.templateSetApi.create, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *updateTemplateSet(payload: { id: string | number, data: any }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.templateSetApi, this.templateSetApi.update, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *removeTemplateSet(id: string | number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.templateSetApi, this.templateSetApi.remove, id);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *listTemplateMotifs(payload?: { template_set_id?: number | string, include_sponsor_disabled?: 0 | 1, include_inactive?: 0 | 1 }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.templateMotifApi, this.templateMotifApi.list, payload);
            if (res?.ok) this.templateMotifs = res.data?.items ?? [];
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *createTemplateMotif(payload: { template_set_id: string | number, data: any }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.templateMotifApi, this.templateMotifApi.create, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *updateTemplateMotif(payload: { id: string | number, data: any }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.templateMotifApi, this.templateMotifApi.update, payload);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }

    *removeTemplateMotif(id: string | number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.templateMotifApi, this.templateMotifApi.remove, id);
            return res;
        } catch (e: any) {
            return e?.data;
        }
    }
}
